import {
    clamp01,
    interpolate,
    tokenState
} from "./animation-keys.js";

// Total length of the CSS animation; CSS delays/durations below are in
// seconds and converted to normalised time with `at()`.
const DURATION = 1.3;
const at = (seconds) => seconds / DURATION;

// Mirrors the demon-kill-recoil keyframes: [t, x%, y%, rotation deg].
const RECOIL_KEYS = [
    [0.00, 0, 0, 0],
    [0.30, 0, 0, 0],
    [0.36, 2, 4, -4],
    [0.42, 2, 6, -6],
    [0.46, -2, 6, 3],
    [0.50, 2, 5, -3],
    [0.54, -1, 4, 2],
    [0.65, 0, 2, 0],
    [1.00, 0, 0, 0]
];

// Mirrors the demon-kill-darken keyframes: [t, grayscale, brightness, red glow].
const DARKEN_KEYS = [
    [0.00, 0, 1, 0],
    [0.30, 0.2, 0.7, 0],
    [0.42, 0.2, 0.7, 0],
    [0.50, 0.3, 1.2, 1],
    [1.00, 1, 0.8, 0]
];

// Mirrors the demon-silhouette keyframes over its 0.75s window:
// [t, opacity, translateY, scale].
const SILHOUETTE_END = at(0.75);
const SILHOUETTE_KEYS = [
    [0.00, 0, -12, 0.85],
    [0.52, 0.9, 0, 1],
    [0.72, 0.95, 10, 1.3],
    [1.00, 0, 14, 1.4]
];

// Claw paths as quadratic curves [x0, y0, cx, cy, x1, y1] with their CSS
// animation delays; each slash takes 0.1s.
const CLAWS = [
    { path: [70, 22, 52, 44, 30, 66], delay: 0.55 },
    { path: [78, 32, 60, 54, 38, 76], delay: 0.59 },
    { path: [84, 44, 68, 64, 48, 84], delay: 0.62 }
];
const CLAW_DURATION = 0.1;
const CLAW_COLOR = "#a3101a";
const DRIP_LENGTH = 10;

const SILHOUETTE_PATH = (
    "M18 50 C20 40 30 34 40 33 C37 29 37 22 40 18 C36 14 33 9 32 3 "
    + "C38 8 42 12 45 15 C48 14 52 14 55 15 C58 12 62 8 68 3 "
    + "C67 9 64 14 60 18 C63 22 63 29 60 33 C70 34 80 40 82 50 Z"
);

/**
 * Returns the transform/filter the killed token should be drawn with at
 * normalised time `t` (0-1), matching the CSS keyframes.
 *
 * @param  {Number} t
 * @return {Object}
 *         {x, y} offsets as fractions of the token size, `rotate` in degrees,
 *         `filter` as a canvas filter string.
 */
export function tokenStateAt(t) {
    return tokenState(interpolate(RECOIL_KEYS, t), interpolate(DARKEN_KEYS, t));
}

// demon-shadow-rise: 0.39s ease-out from the start.
const vignetteAt = (t) => 1 - (1 - clamp01(t / at(0.39))) ** 2;

// demon-eyes: 0.39s from the start, hidden for the first 38%.
const eyesAt = (t) => clamp01((t - at(0.39) * 0.38) / (at(0.39) * 0.62));

// demon-drip: 0.29s ease-in starting at 0.75s.
const dripAt = (t) => clamp01((t - at(0.75)) / at(0.29)) ** 2;

// demon-kill-fade-out: 0.26s ease-in starting at 1.04s.
const opacityAt = (t) => 1 - clamp01((t - at(1.04)) / at(0.26));

const quadraticPoint = ([x0, y0, cx, cy, x1, y1], p) => [
    (1 - p) ** 2 * x0 + 2 * (1 - p) * p * cx + p ** 2 * x1,
    (1 - p) ** 2 * y0 + 2 * (1 - p) * p * cy + p ** 2 * y1
];

/**
 * Draws the demon attack scene at normalised animation time `t`, matching
 * the inline SVG in the character template. Coordinates are in the same
 * 0-100 space as the SVG viewBox.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {Number} x
 *        Left edge of the token area in canvas pixels.
 * @param {Number} y
 *        Top edge of the token area in canvas pixels.
 * @param {Number} size
 *        Token size in canvas pixels.
 * @param {Number} t
 *        Normalised animation time, 0-1.
 */
export function drawDemonKill(context, x, y, size, t) {

    context.save();
    context.translate(x, y);
    context.scale(size / 100, size / 100);
    context.globalAlpha = opacityAt(t);

    // Darkness closing in around the token.
    const gradient = context.createRadialGradient(50, 50, 0, 50, 50, 50);
    const shade = 0.85 * vignetteAt(t);
    gradient.addColorStop(0.45, "rgb(20 0 30 / 0)");
    gradient.addColorStop(1, `rgb(20 0 30 / ${shade})`);
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(50, 50, 50, 0, Math.PI * 2);
    context.fill();

    // Horned silhouette rising and lunging.
    const [opacity, shift, scale] = interpolate(
        SILHOUETTE_KEYS,
        clamp01(t / SILHOUETTE_END)
    );

    if (opacity > 0.01) {

        context.save();
        context.globalAlpha *= opacity;
        context.translate(50, 30 + shift);
        context.scale(scale, scale);
        context.translate(-50, -30);
        context.fillStyle = "#1a0a1f";
        context.fill(new Path2D(SILHOUETTE_PATH));

        const eyes = eyesAt(t);

        if (eyes > 0.01) {
            context.globalAlpha *= eyes;
            context.fillStyle = "#ff2a2a";
            context.shadowColor = "#ff2a2a";
            context.shadowBlur = 3 * size / 100;
            [45, 55].forEach((cx) => {
                context.beginPath();
                context.arc(cx, 24, 1.8, 0, Math.PI * 2);
                context.fill();
            });
        }

        context.restore();

    }

    // Claw slashes and the blood running from them.
    context.strokeStyle = CLAW_COLOR;
    context.lineCap = "round";

    CLAWS.forEach(({ path, delay }) => {

        const progress = clamp01((t - at(delay)) / at(CLAW_DURATION));

        if (progress <= 0) {
            return;
        }

        context.lineWidth = 4;
        context.beginPath();
        context.moveTo(path[0], path[1]);
        for (let i = 1; i <= 16; i += 1) {
            context.lineTo(...quadraticPoint(path, progress * i / 16));
        }
        context.stroke();

        const drip = dripAt(t);

        if (drip > 0) {
            context.lineWidth = 2.5;
            context.beginPath();
            context.moveTo(path[4], path[5]);
            context.lineTo(path[4], path[5] + DRIP_LENGTH * drip);
            context.stroke();
        }

    });

    context.restore();

}
